import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Send, CheckCircle2, XCircle, Trash2, Eye, ArrowRightLeft } from "lucide-react";

const STATUS_META = {
  draft: { label: "Borrador", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved: { label: "Aprobada", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  rejected: { label: "Rechazada", cls: "bg-red-100 text-red-800 border-red-300" },
  cancelled: { label: "Cancelada", cls: "bg-slate-100 text-slate-500 border-slate-300" },
  fulfilled: { label: "Completada", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const EMPTY_FORM = { from_warehouse: "", to_warehouse: "", priority: "med", justification: "",
                     equipment_id: "", wo_id: "", lines: [{ item_number: "", qty: 1, uom: "", notes: "" }] };

export default function InternalRequisitions() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    api.get("/internal-requisitions", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    try {
      const payload = { ...form,
        lines: form.lines.filter((l) => l.item_number && Number(l.qty) > 0)
                          .map((l) => ({ ...l, qty: Number(l.qty) })) };
      if (!payload.from_warehouse || !payload.to_warehouse) throw new Error("Almacenes requeridos");
      if (!payload.lines.length) throw new Error("Al menos 1 línea con artículo y cantidad");
      await api.post("/internal-requisitions", payload);
      toast.success("Requisición interna creada (borrador)");
      setOpenNew(false); setForm(EMPTY_FORM); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submit = async (id) => {
    try {
      await api.post(`/internal-requisitions/${id}/submit`);
      toast.success("Enviada a aprobación");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const approve = async (id) => {
    if (!window.confirm("¿Aprobar? Se crearán las transferencias automáticamente.")) return;
    try {
      await api.post(`/internal-requisitions/${id}/approve`);
      toast.success("Aprobada · transferencias creadas");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Motivo de rechazo:");
    if (!reason || reason.length < 3) return;
    try {
      await api.post(`/internal-requisitions/${id}/reject`, { reason });
      toast.success("Rechazada");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cancel = async (id) => {
    const reason = window.prompt("Motivo de cancelación:");
    if (reason === null) return;
    try {
      await api.post(`/internal-requisitions/${id}/cancel`, { reason: reason || "sin motivo" });
      toast.success("Cancelada");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/internal-requisitions/${id}`);
      setDetail(data);
      setDetailOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, { item_number: "", qty: 1, uom: "", notes: "" }] });
  const setLine = (i, k, v) => setForm({ ...form, lines: form.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const isWarehouse = can("admin", "warehouse");

  return (
    <div data-testid="internal-req-page">
      <PageHeader title="Requisiciones Internas" subtitle="Solicita material desde otro almacén con flujo aprobación → transferencia.">
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-none text-xs font-bold" data-testid="ir-new-button">
              <Plus size={13} className="mr-1" /> Nueva Requisición
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="uppercase font-black">Nueva Requisición Interna</DialogTitle>
              <DialogDescription className="text-xs">Solicita material desde otro almacén. Al aprobarse se genera la transferencia automáticamente.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase">Origen (desde)</Label>
                <Select value={form.from_warehouse} onValueChange={(v) => setForm({ ...form, from_warehouse: v })}>
                  <SelectTrigger className="rounded-none h-9 text-xs" data-testid="ir-from-warehouse"><SelectValue placeholder="Almacén origen" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase">Destino (para)</Label>
                <Select value={form.to_warehouse} onValueChange={(v) => setForm({ ...form, to_warehouse: v })}>
                  <SelectTrigger className="rounded-none h-9 text-xs" data-testid="ir-to-warehouse"><SelectValue placeholder="Almacén destino" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.filter((w) => w.code !== form.from_warehouse).map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] font-bold uppercase">Justificación</Label>
                <Textarea rows={2} value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })}
                          className="rounded-none text-xs" data-testid="ir-justification"
                          placeholder="Ej. Requerido para OT-00050 falla motor CT-01" />
              </div>
            </div>

            <div className="mt-3">
              <div className="flex justify-between items-center mb-2">
                <div className="text-[10px] font-bold uppercase tracking-widest">Líneas ({form.lines.length})</div>
                <Button size="sm" variant="outline" onClick={addLine} className="rounded-none text-[10px] h-7 font-bold" data-testid="ir-add-line">
                  <Plus size={11} className="mr-0.5" /> Agregar
                </Button>
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead><tr><th>Artículo</th><th className="num">Cantidad</th><th>UOM</th><th>Notas</th><th></th></tr></thead>
                  <tbody>
                    {form.lines.map((ln, i) => (
                      <tr key={i}>
                        <td>
                          <Select value={ln.item_number} onValueChange={(v) => setLine(i, "item_number", v)}>
                            <SelectTrigger className="rounded-none h-7 text-[11px]" data-testid={`ir-line-item-${i}`}><SelectValue placeholder="Elegir artículo" /></SelectTrigger>
                            <SelectContent>
                              {items.map((it) => <SelectItem key={it.item_number} value={it.item_number}>{it.item_number} — {it.description}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td><Input type="number" min={0.01} step="any" value={ln.qty} onChange={(e) => setLine(i, "qty", e.target.value)}
                                    className="rounded-none h-7 text-[11px] tabular-nums font-bold" data-testid={`ir-line-qty-${i}`} /></td>
                        <td><Input value={ln.uom} onChange={(e) => setLine(i, "uom", e.target.value)}
                                    placeholder="EA" className="rounded-none h-7 text-[11px]" /></td>
                        <td><Input value={ln.notes} onChange={(e) => setLine(i, "notes", e.target.value)}
                                    className="rounded-none h-7 text-[11px]" /></td>
                        <td>
                          {form.lines.length > 1 && (
                            <button onClick={() => removeLine(i)} className="text-destructive hover:opacity-70" data-testid={`ir-line-remove-${i}`}>
                              <Trash2 size={11} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-none text-xs">Cancelar</Button>
              <Button onClick={create} className="rounded-none text-xs font-bold" data-testid="ir-create-submit">Crear (borrador)</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Estado</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="ir-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.keys(STATUS_META).map((k) => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {rows.length === 0 ? <Empty text="Sin requisiciones internas" /> : (
        <div className="tbl-wrap" data-testid="ir-table">
          <table className="tbl">
            <thead><tr>
              <th>Número</th><th>Ruta</th><th>Solicitante</th><th className="num">Líneas</th>
              <th>Estado</th><th>Creada</th><th>Acciones</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.draft;
                return (
                  <tr key={r.id} data-testid={`ir-row-${r.number}`}>
                    <td className="font-bold tabular-nums">{r.number}</td>
                    <td><span className="text-[11px]"><b>{r.from_warehouse}</b> <ArrowRightLeft size={10} className="inline mx-1" /> <b>{r.to_warehouse}</b></span></td>
                    <td className="text-[11px]">{r.requester_name || r.requester_email}</td>
                    <td className="num tabular-nums">{r.lines?.length || 0}</td>
                    <td><span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 border ${meta.cls}`}>{meta.label}</span></td>
                    <td className="text-[10px] text-muted-foreground">{fmtDate(r.created_at)}</td>
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => openDetail(r.id)} className="text-primary hover:opacity-70" title="Ver" data-testid={`ir-view-${r.number}`}>
                          <Eye size={12} />
                        </button>
                        {r.status === "draft" && (
                          <button onClick={() => submit(r.id)} className="text-primary hover:opacity-70" title="Enviar" data-testid={`ir-submit-${r.number}`}>
                            <Send size={12} />
                          </button>
                        )}
                        {r.status === "pending" && isWarehouse && (
                          <>
                            <button onClick={() => approve(r.id)} className="text-emerald-700 hover:opacity-70" title="Aprobar" data-testid={`ir-approve-${r.number}`}>
                              <CheckCircle2 size={12} />
                            </button>
                            <button onClick={() => reject(r.id)} className="text-destructive hover:opacity-70" title="Rechazar" data-testid={`ir-reject-${r.number}`}>
                              <XCircle size={12} />
                            </button>
                          </>
                        )}
                        {["draft", "pending"].includes(r.status) && (
                          <button onClick={() => cancel(r.id)} className="text-muted-foreground hover:text-destructive" title="Cancelar" data-testid={`ir-cancel-${r.number}`}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ir-detail-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">Requisición Interna {detail?.number}</DialogTitle>
          </DialogHeader>
          {detail && (
            <>
              <div className="grid grid-cols-3 gap-2 text-[11px] mb-3">
                <div><b className="uppercase text-[9px] text-muted-foreground">Ruta:</b><br/>{detail.from_warehouse} → {detail.to_warehouse}</div>
                <div><b className="uppercase text-[9px] text-muted-foreground">Solicitante:</b><br/>{detail.requester_email}</div>
                <div><b className="uppercase text-[9px] text-muted-foreground">Estado:</b><br/>
                  <span className={`inline-block text-[10px] font-black uppercase px-1.5 py-0.5 border ${(STATUS_META[detail.status] || STATUS_META.draft).cls}`}>
                    {(STATUS_META[detail.status] || {}).label || detail.status}
                  </span>
                </div>
                {detail.justification && <div className="col-span-3"><b className="uppercase text-[9px] text-muted-foreground">Justificación:</b> {detail.justification}</div>}
                {detail.rejection_reason && <div className="col-span-3 text-red-700"><b>Rechazo:</b> {detail.rejection_reason}</div>}
              </div>

              <div className="tbl-wrap mb-3">
                <table className="tbl">
                  <thead><tr><th>Artículo</th><th>Descripción</th><th className="num">Cantidad</th><th>UOM</th><th>Transferencia</th></tr></thead>
                  <tbody>
                    {detail.lines.map((ln, i) => (
                      <tr key={i}>
                        <td className="font-mono text-[10px]">{ln.item_number}</td>
                        <td>{ln.description}</td>
                        <td className="num tabular-nums font-bold">{ln.qty}</td>
                        <td>{ln.uom}</td>
                        <td className="text-primary font-bold">{ln.transfer_number || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detail.history?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground mb-1">Historial</div>
                  <div className="space-y-1">
                    {detail.history.map((h, i) => (
                      <div key={i} className="text-[10px] border-l-2 border-primary pl-2">
                        <b>{h.action}</b> · {h.by} · {fmtDate(h.at)}
                        {h.reason && <span className="text-muted-foreground"> · {h.reason}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
